// Running Order, the daily (CLAUDE.md: Core rules, Game screen order).
//
// Header, instruction line, the board, Submit directly under it at the same gap,
// the guesses counter. No large question heading — the instruction line is the
// only prompt.
//
// Three guesses. After each, every poster in its correct position locks and
// stays locked. A repeated arrangement is allowed: Submit stays live and nothing
// is blocked or warned about. What is not allowed is spending two guesses on one
// press, so a submission in flight ignores the next one.
//
// **The answer is never on this device until the game is over.** The board comes
// from the server without years (ro_board), every guess is checked by the server
// (ro_check), and the correct order arrives only with the last verdict. So this
// screen cannot mark a guess itself: it sends every arrangement submitted so far
// and shows what comes back.
//
// A puzzle is played once, and it is picked up where it was left. The current
// arrangement and the arrangements submitted are kept on this device; on the way
// back in, the server is asked again, so a refresh mid-game returns the same
// board and a refresh after the last guess returns the result screen.
//
// **A replay is the one exception** (`/running-order/12?replay`, owner, 13
// October), reached by tapping a played tile in the archive: the stored shuffle
// again, nothing saved, nothing recorded, nothing towards stats or anybody's
// statistics, and no Share on its result screen. The first play's result stays
// exactly as it was, and is what a link or a next-game button opens. It is
// honoured only for a puzzle this device has finished; on any other the flag is
// ignored and the puzzle plays normally.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { GameHeader } from '../../components/GameHeader.tsx';
import { Notice } from '../../pages/Notice.tsx';
import { puzzleNumber } from '../../lib/puzzle-number.ts';
import { Board } from './Board.tsx';
import { Result } from './Result.tsx';
import { MAX_GUESSES, type CheckResult, type RunningOrderBoard } from './types.ts';
import { loadProgress, lockedFrom, saveProgress } from './progress.ts';
import { PREVIEW, previewGuesses } from '../../dev/preview.ts';
import { outcomes, recordFinished } from '../../lib/history.ts';
import { wasWithdrawn } from '../../lib/archive.ts';
import { todaysNumber } from './today.ts';
import { rpc } from '../../lib/api.ts';
import { useResultFromTop } from '../../lib/finish.ts';
import './RunningOrderGame.css';

type Loaded =
  | { state: 'loading' }
  | { state: 'none'; number: number | null; withdrawn?: boolean }
  | { state: 'error'; message: string }
  | { state: 'ready'; board: RunningOrderBoard; order: number[]; submitted: number[][]; verdict: CheckResult | null };

const check = (number: number, guesses: number[][]) =>
  rpc<CheckResult>('ro_check', { p_number: number, p_guesses: guesses });

/** Today's Running Order, or one by number: loaded, then played. */
export function RunningOrderPage() {
  const params = useParams();
  // No number: today's daily, or the next Call Sheet. A number that is not one —
  // "abc", "0", "-3", "2.5" — is an address that does not exist (lib/puzzle-number.ts).
  const wanted = params.number === undefined ? null : puzzleNumber(params.number);
  const badAddress = params.number !== undefined && wanted === null;
  const [search] = useSearchParams();
  const replay = search.has('replay') && wanted !== null && outcomes('running-order').has(wanted);
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });

  useEffect(() => {
    if (badAddress) return;
    let live = true;
    setLoaded({ state: 'loading' });
    (async (): Promise<Loaded> => {
      const board = await rpc<RunningOrderBoard | null>('ro_board', { p_number: wanted });
      if (!board) return { state: 'none', number: wanted, withdrawn: wanted !== null && await wasWithdrawn('running-order', wanted) };
      // Preview mode starts finished, on invented marks; it asks for the answer
      // the only way the server gives one, by spending all three guesses.
      if (PREVIEW) {
        const ended = await check(board.number, [board.startOrder, board.startOrder, board.startOrder]);
        const marks = previewGuesses(board.films.length);
        return { state: 'ready', board, order: board.startOrder, submitted: [],
                 verdict: { ...ended, marks, won: true } };
      }
      // A replay starts from the stored shuffle, whatever this device has kept.
      if (replay) return { state: 'ready', board, order: board.startOrder, submitted: [], verdict: null };
      const saved = loadProgress(board.number, board.films.map((f) => f.id));
      const submitted = saved?.submitted ?? [];
      const verdict = submitted.length ? await check(board.number, submitted) : null;
      return { state: 'ready', board, order: saved?.order ?? board.startOrder, submitted, verdict };
    })().then(
      (next) => { if (live) setLoaded(next); },
      (err: Error) => { if (live) setLoaded({ state: 'error', message: err.message }); }
    );
    return () => { live = false; };
  }, [wanted, badAddress, replay]);

  if (badAddress) return <Notice message="That page doesn't exist." />;
  if (loaded.state === 'loading') return <main className="column ro-game" aria-busy="true" />;
  if (loaded.state === 'error') {
    return <Notice game="running-order" message={`Something went wrong loading the puzzle. ${loaded.message}`} />;
  }
  if (loaded.state === 'none') {
    return loaded.number === null
      ? <Notice game="running-order" message="Running Order starts soon. There is no puzzle today yet." />
      : loaded.withdrawn
        ? <Notice game="running-order" message={`Running Order #${loaded.number} is no longer available.`} />
        : <Notice game="running-order" message={`There is no Running Order #${loaded.number} yet.`} />;
  }
  return <RunningOrderGame key={`${loaded.board.number}${replay ? '-replay' : ''}`} {...loaded} replay={replay} />;
}

interface GameProps {
  board: RunningOrderBoard;
  order: number[];
  submitted: number[][];
  verdict: CheckResult | null;
  /** A replay from the archive: nothing saved, nothing recorded, no Share. */
  replay?: boolean;
}

export function RunningOrderGame({ board, order: initialOrder, submitted: initialSubmitted, verdict: initialVerdict, replay = false }: GameProps) {
  const [order, setOrder] = useState<number[]>(initialOrder);
  const [submitted, setSubmitted] = useState<number[][]>(initialSubmitted);
  const [verdict, setVerdict] = useState<CheckResult | null>(initialVerdict);
  const [problem, setProblem] = useState<string | null>(null);
  const submitting = useRef(false);

  const marks = verdict?.marks ?? [];
  // Locked is a consequence of the marks, not a third thing to keep in step.
  const locked = useMemo(() => lockedFrom(marks, board.films.length), [marks, board.films.length]);

  // Written back on every change. A board touched but not yet guessed on is
  // worth keeping too — a player who has dragged three cards and reloaded wants
  // them where they left them — but an untouched one is not worth a record.
  const touched = submitted.length > 0 || order.some((id, i) => id !== board.startOrder[i]);
  useEffect(() => {
    if (PREVIEW || replay || !touched) return;
    saveProgress(board.number, order, submitted);
  }, [board.number, order, submitted, touched, replay]);

  // **A finished game is recorded** — here, for the Stats window and the
  // archive, and on the server, for everybody's statistics (lib/history.ts).
  // Whether it counts is the server's to say; until it answers, it counts if it
  // is today's daily. Nothing is recorded twice, so a reload of a finished game
  // posts nothing.
  useEffect(() => {
    if (PREVIEW || replay || !verdict?.finished) return;
    const solvedOn = verdict.won ? verdict.marks.length : null;
    recordFinished('running-order', board.number, submitted, solvedOn,
      todaysNumber().then((today) => today === board.number));
  }, [verdict, board.number, submitted, replay]);

  function swap(a: number, b: number) {
    setOrder((current) => {
      const next = [...current];
      [next[a], next[b]] = [next[b]!, next[a]!];
      return next;
    });
  }

  async function submit() {
    if (verdict?.finished || submitting.current) return;
    submitting.current = true;
    setProblem(null);
    const next = [...submitted, [...order]];
    try {
      const result = await check(board.number, next);
      // Only a verdict spends the guess: a failed request leaves the board as
      // it was, to be submitted again.
      setSubmitted(next);
      setVerdict(result);
    } catch {
      setProblem("Couldn't check that guess. Check your connection and press Submit again.");
    } finally {
      submitting.current = false;
    }
  }

  // Finishing stays here, and the result screen opens at its top.
  useResultFromTop(Boolean(verdict?.finished));

  if (verdict?.finished && verdict.answer) {
    return <Result number={board.number} answer={verdict.answer} won={verdict.won} guesses={verdict.marks}
                   submitted={submitted} replay={replay} />;
  }

  const remaining = MAX_GUESSES - marks.length;

  return (
    <main className="column ro-game">
      <GameHeader game="Running Order" number={board.number} inBar />

      {/* The instruction and the board, grouped only so the line sits in one
          place across all three states — playing, won and lost. No panel: the
          board is the screen, and a box around it was another edge competing
          with the cards inside it. */}
      <section className="ro-play">
        <p className="panel-line">Drag into release order, oldest first.</p>
        <Board films={board.films} order={order} locked={locked} onSwap={swap} />
      </section>

      <button type="button" className="btn btn-primary btn-block ro-submit" onClick={submit}>Submit</button>

      {/* Running Order shows no guess list in play — the board already carries
          the answer so far, in the cards that locked. The one thing it says is
          when a guess has placed nothing at all, which the board cannot show. */}
      <div className="ro-counter">
        {problem && <p className="ro-none-correct" role="alert">{problem}</p>}
        {!problem && marks.length > 0 && !locked.some(Boolean) && (
          <p className="ro-none-correct" role="status">None correct</p>
        )}
        {/* Plural at every value, centred under Submit, as a caption. */}
        <span className="caption ro-remaining">Guesses remaining: {remaining}</span>
      </div>
    </main>
  );
}
